import { createRef } from "react";
import { Button, Spinner } from "react-bootstrap";
import { X } from "react-bootstrap-icons";
import NumberFormat from "react-number-format";
import ChangeHighlight from "react-change-highlight";
import BlockyHeavyText from "components/custom-texts/blocky-heavy-text";
import BlockyBoldText from "components/custom-texts/blocky-bold-text";
import BlockyMediumText from "components/custom-texts/blocky-medium-text";
import { secondaryColor } from "utils/constants";
import styles from "assets/shared-stylesheets/sparket-list-item.module.css";
import highlightStyles from "components/pool-details/bet-modal/bet-modal-content/styles.module.css";
import BackCardShell from "./BackCardShell";
import { BackCardProps } from "./types";

/**
 * Mirror of the current production back card layout (HomepageBetForm).
 * Everything yellow / secondaryColor.
 */
const VariantBaseline = (props: BackCardProps) => {
  const {
    eventName,
    poolName,
    winCondition,
    contestantName,
    isBasic,
    picksRemaining,
    expReturn,
    balanceLabel,
    balanceValue,
    oddsLabel,
    betAmount,
    onBetAmountChange,
    onConfirm,
    onCancel,
    loading,
    disabled,
  } = props;

  return (
    <BackCardShell>
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <div>
          <BlockyHeavyText style={{ fontSize: 10 }}>{eventName}</BlockyHeavyText>
          <BlockyHeavyText className={styles.poolNameText}>
            {poolName}
          </BlockyHeavyText>
        </div>
        <div style={{ marginTop: -5, cursor: "pointer" }} onClick={onCancel}>
          <X size={30} />
        </div>
      </div>

      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <div>
          <BlockyHeavyText className={styles.poolCloseDatePrefix}>
            Win Condition:&nbsp;
          </BlockyHeavyText>
          <BlockyMediumText
            style={{ fontSize: 12, display: "inline", lineHeight: 0.5 }}
          >
            {winCondition}
          </BlockyMediumText>
        </div>
      </div>

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-end",
        }}
      >
        <div style={{ overflow: "hidden" }}>
          <BlockyHeavyText style={{ fontSize: 16, color: secondaryColor }}>
            {contestantName}
          </BlockyHeavyText>
        </div>
        <div>
          <BlockyHeavyText className={styles.poolCloseDatePrefix}>
            {isBasic ? "Picks Remaining:" : balanceLabel || "Balance:"}&nbsp;
          </BlockyHeavyText>
          <BlockyBoldText style={{ fontSize: 12, color: secondaryColor }}>
            {isBasic ? picksRemaining ?? 0 : balanceValue}
          </BlockyBoldText>
        </div>
      </div>

      <div>
        {isBasic ? (
          <div style={{ padding: 3 }}>
            <Button
              style={{
                width: "100%",
                height: 41,
                backgroundColor: secondaryColor,
                color: "black",
              }}
              disabled={loading || disabled}
              onClick={onConfirm}
            >
              {loading && (
                <Spinner
                  animation="border"
                  size="sm"
                  style={{ marginRight: 10 }}
                />
              )}
              Confirm
            </Button>
            <div
              style={{
                marginTop: 7,
                display: "flex",
                alignItems: "flex-end",
              }}
            >
              <BlockyBoldText>Exp. Return: &nbsp;</BlockyBoldText>
              <BlockyBoldText>
                <ChangeHighlight
                  hideAfter={1000}
                  highlightClassName={highlightStyles.highlight}
                >
                  <span
                    ref={createRef()}
                    style={{ color: secondaryColor, fontSize: 16 }}
                  >
                    {expReturn}
                  </span>
                </ChangeHighlight>
              </BlockyBoldText>
            </div>
          </div>
        ) : (
          <div style={{ display: "flex", padding: 3 }}>
            <div style={{ flex: 0.5 }}>
              <NumberFormat
                value={betAmount}
                onValueChange={(v) => onBetAmountChange?.(v.formattedValue)}
                prefix="§"
                decimalScale={2}
                thousandSeparator
                inputMode="decimal"
                fixedDecimalScale
                allowNegative={false}
                placeholder="Enter Amount"
                style={{
                  borderRadius: 5,
                  height: 40,
                  color: "black",
                  width: "95%",
                  fontSize: 16,
                }}
              />
              <div style={{ display: "flex" }}>
                <BlockyBoldText
                  style={{
                    marginTop: 7,
                    display: "flex",
                    alignItems: "flex-end",
                  }}
                >
                  Odds: &nbsp;
                  <ChangeHighlight
                    hideAfter={1000}
                    highlightClassName={highlightStyles.highlight}
                  >
                    <div
                      ref={createRef()}
                      style={{ color: secondaryColor, fontSize: 16 }}
                    >
                      {oddsLabel}
                    </div>
                  </ChangeHighlight>
                </BlockyBoldText>
              </div>
            </div>
            <div style={{ width: "100%", flex: 0.5 }}>
              <Button
                style={{
                  width: "100%",
                  height: 41,
                  backgroundColor: secondaryColor,
                  color: "black",
                }}
                disabled={loading || disabled}
                onClick={onConfirm}
              >
                {loading && (
                  <Spinner
                    animation="border"
                    size="sm"
                    style={{ marginRight: 10 }}
                  />
                )}
                Confirm
              </Button>
              <div style={{ marginTop: 7 }}>
                <div style={{ display: "flex", alignItems: "flex-end" }}>
                  <BlockyBoldText>Exp. Return: &nbsp;</BlockyBoldText>
                  <BlockyBoldText>
                    <ChangeHighlight
                      hideAfter={1000}
                      highlightClassName={highlightStyles.highlight}
                    >
                      <span
                        ref={createRef()}
                        style={{ color: secondaryColor, fontSize: 16 }}
                      >
                        {expReturn}
                      </span>
                    </ChangeHighlight>
                  </BlockyBoldText>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      <BlockyMediumText style={{ fontSize: 9, color: "gray" }}>
        Expected returns are subject to change based on subsequent activity in
        the pool.
      </BlockyMediumText>
    </BackCardShell>
  );
};

export default VariantBaseline;
